/**
 * MarkdownDescription — one TipTap editor that renders task descriptions as
 * markdown. `editable=false` is the read-only formatted view (click to edit);
 * `editable=true` is the Notion-style WYSIWYG. Markdown round-trips through
 * `task.description` — the DB/API shape is unchanged.
 *
 * ⌘↵ / Ctrl↵ saves (serialises to markdown, empty → null); Esc reverts+exits.
 * Hosts that need to save from outside the editor (e.g. the preview drawer's
 * "Save and continue" on its unsaved-changes dialog) pass a `ref` and call
 * `save()`.
 */
import { useCallback, useEffect, useImperativeHandle, useRef, type Ref } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown, type MarkdownStorage } from 'tiptap-markdown';
import styles from './MarkdownDescription.module.css';

// tiptap-markdown (built for TipTap v2) doesn't augment v3's empty `Storage`
// interface, and @tiptap/core isn't a direct dep here so module augmentation
// won't resolve — read its typed storage through a narrow cast instead.
type EditorWithMarkdown = { storage: { markdown: MarkdownStorage } };
const getMarkdown = (editor: EditorWithMarkdown): string =>
  editor.storage.markdown.getMarkdown();

export interface MarkdownDescriptionHandle {
  /** Serialise the current content and hand it to `onSave` — same as ⌘↵. */
  save: () => void;
}

export interface MarkdownDescriptionProps {
  value: string | null;
  editable: boolean;
  /** Called with markdown (empty → null) when the user saves (⌘↵). Omit on
   *  surfaces that only display the description (see `onRequestEdit`). */
  onSave?: (markdown: string | null) => void;
  /** Called when the user starts editing (click on the read-only view).
   *  Omitting it makes the read-only view a plain, non-focusable display —
   *  no cursor, hover or Enter affordance to compete with the host screen's
   *  own keyboard shortcuts (e.g. the Inbox, where ⏎ means Accept). */
  onRequestEdit?: () => void;
  /** Called on Esc — caller should flip `editable` back to false. */
  onCancelEdit?: () => void;
  /** Fires while editing whenever the unsaved-vs-baseline dirty state changes. */
  onDirtyChange?: (dirty: boolean) => void;
  placeholder?: string;
  ref?: Ref<MarkdownDescriptionHandle>;
}

export function MarkdownDescription({
  value,
  editable,
  onSave,
  onRequestEdit,
  onCancelEdit,
  onDirtyChange,
  placeholder = 'Add a description, paste a link, dump context…',
  ref,
}: MarkdownDescriptionProps) {
  // Refs so the editor's stable callbacks read current props.
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const editableRef = useRef(editable);
  editableRef.current = editable;

  // Dirty is measured against the editor's OWN serialisation of the loaded
  // description, never the raw `value` string. Markdown round-trips through
  // TipTap are normalised (list markers, escaping, blank lines, hard breaks),
  // so comparing serialised output to the stored markdown reports "dirty" for
  // descriptions nobody has touched.
  const baselineRef = useRef('');
  // The raw `value` currently loaded into the editor. Lets the sync effect
  // below spot a genuine external change without comparing across formats.
  const appliedRef = useRef(value);
  // Markdown the editor last emitted via onSave, so the sync effect can tell
  // "the editor is ahead of the cache" (just saved, PATCH still in flight)
  // apart from "an external change arrived" — and not clobber freshly-typed
  // content back to the stale value during the save round-trip.
  const lastSavedRef = useRef<string | null>(null);

  const editor = useEditor({
    editable,
    extensions: [
      // TipTap v3 StarterKit bundles the Link extension, so configure it here
      // rather than adding a second Link extension (which would duplicate it).
      StarterKit.configure({ link: { openOnClick: false, autolink: true } }),
      Markdown.configure({ html: false, linkify: true, breaks: true }),
    ],
    content: value ?? '',
    editorProps: {
      attributes: { 'data-placeholder': placeholder },
    },
    onCreate: ({ editor }) => {
      baselineRef.current = getMarkdown(editor as unknown as EditorWithMarkdown);
    },
    onUpdate: ({ editor }) => {
      // Programmatic content loads below pass `emitUpdate: false`, so updates
      // reaching here are the user's own typing — and only edit mode can
      // produce unsaved changes, so never report dirty outside it.
      if (!editableRef.current) return;
      const md = getMarkdown(editor as unknown as EditorWithMarkdown);
      onDirtyChangeRef.current?.(md.trim() !== baselineRef.current.trim());
    },
  });

  const save = useCallback(() => {
    if (!editor) return;
    const raw = getMarkdown(editor as unknown as EditorWithMarkdown);
    lastSavedRef.current = raw;
    baselineRef.current = raw;
    const md = raw.trim();
    onSaveRef.current?.(md.length ? md : null);
  }, [editor]);

  useImperativeHandle(ref, () => ({ save }), [save]);

  // Keep editability in sync without remounting (seamless view↔edit). Entering
  // edit mode re-baselines against what's on screen, so "dirty" means "changed
  // since the editor opened"; any transition also clears the dirty signal.
  // `setEditable` emits an update event by default — suppress it, it isn't a
  // content change.
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable, false);
    if (editable) {
      baselineRef.current = getMarkdown(editor as unknown as EditorWithMarkdown);
      editor.commands.focus('end');
    }
    onDirtyChangeRef.current?.(false);
  }, [editable, editor]);

  // When not editing, reflect genuine external value changes (e.g. a cache
  // refresh from another surface). Compare raw value against raw value: the
  // editor's serialisation legitimately differs from the stored markdown, and
  // reloading the document on that difference re-set the content on every
  // render pass — which (updates being emitted) is what marked untouched
  // descriptions dirty and popped the discard dialog on task switches.
  useEffect(() => {
    if (!editor || editable) return;
    if (value === appliedRef.current) return;
    const current = getMarkdown(editor as unknown as EditorWithMarkdown);
    if (current === lastSavedRef.current) {
      // Our own save just landed in the cache; the editor already shows it.
      appliedRef.current = value;
      return;
    }
    editor.commands.setContent(value ?? '', { emitUpdate: false });
    appliedRef.current = value;
    baselineRef.current = getMarkdown(editor as unknown as EditorWithMarkdown);
  }, [value, editable, editor]);

  if (!editor) return null;

  const isEmpty = !(value && value.trim().length);

  if (!editable) {
    const body = isEmpty ? placeholder : <EditorContent editor={editor} />;
    // Display-only when the host didn't ask for an edit hook: skip the
    // clickable chrome entirely rather than render a button that does nothing.
    if (!onRequestEdit) {
      return (
        <div className={`${styles.root} ${styles.prose} ${isEmpty ? styles.empty : ''}`}>
          {body}
        </div>
      );
    }
    return (
      <div
        className={`${styles.root} ${styles.prose} ${styles.display} ${isEmpty ? styles.empty : ''}`}
        onClick={onRequestEdit}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onRequestEdit();
        }}
      >
        {body}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div
        className={`${styles.prose} ${styles.editor}`}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            save();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            editor.commands.setContent(value ?? '', { emitUpdate: false });
            onCancelEdit?.();
          }
        }}
      >
        <EditorContent editor={editor} />
      </div>
      <div className={styles.foot}>
        <span className="kos-kbd">⌘↵</span> save · <span className="kos-kbd">Esc</span> cancel
      </div>
    </div>
  );
}
